// Uhrwerk (I3) — die Gruppe als Getriebe "Kaliber XH-4" (AUFTRAG K3,
// docs/INSTRUMENTE-KATALOG.md I3 + V2 Skeleton-Watch-Ästhetik).
//
// Je Firma ein Zahnrad in Markenfarbe, Drehzahl = Umsatz-Pace vs. VJ
// (paceRatio), alle treiben das Holding-Rad (Indigo, Rubinlager). Firma
// unter Plan (< 0.9): Rad STOCKT sichtbar (play-state ~1s alle ~5s,
// docs/MOTION-CRAFT.md §Zahnräder) + Status-Zeile "…-RAD STOCKT".
// Klick auf ein Rad = echter Flow → Firmen-Detailseite.
//
// Daten-Kontrakt: pace null → Rad steht, Label "KEINE DATEN" · stale →
// Räder drehen mit LETZTER Pace + Header "STAND GESTERN".
//
// K7/Klarheit (Kevin 06.07.): jedes Rad zeigt "XXX % VJ ▲/▼" statt
// abstraktem "U/MIN"; grün = vor Plan, amber = knapp, clay = hinter Plan
// (Rad stockt). Header + Holding-Rad zeigen den Gruppen-Ø. Ein-Satz-Legende
// macht das Instrument in 5 Sekunden lesbar.

import { useEffect, useRef, useState } from "react";

import { FIRMA_COLORS, FIRMA_LABEL, type FirmaSlug } from "../../lib/firma-colors";

export interface RadDaten {
  firma: Extract<FirmaSlug, "drainx" | "gundk" | "infrax">;
  paceRatio: number | null;
}

interface Props {
  raeder: RadDaten[];
  stale?: boolean;
  onFirma: (firma: FirmaSlug) => void;
}

/** Zahnrad-Pfad (V3, poliert): Zähne mit verrundeten Köpfen (Q-Kurven) +
 *  ausgerundeten Zahnlücken — sieht wie ein echtes Uhrenzahnrad aus, statt
 *  wie eine gezackte Trapez-Krone. */
function zahnradPfad(r: number, zaehne: number): string {
  const kopf = r + 5; // Zahnkopf-Radius
  const fuss = r - 1.5; // Zahnfuß (leichte Lücke)
  const p = (rad: number, a: number): string =>
    `${(rad * Math.cos(a)).toFixed(2)} ${(rad * Math.sin(a)).toFixed(2)}`;
  const teile: string[] = [];
  for (let i = 0; i < zaehne; i++) {
    // Ein Zahn = Flanke hoch (Q) · verrundeter Kopf · Flanke runter (Q) · Fuß.
    const a0 = (i / zaehne) * Math.PI * 2; // Fuß-Start
    const aF1 = ((i + 0.2) / zaehne) * Math.PI * 2; // Kopf-Beginn
    const aK = ((i + 0.5) / zaehne) * Math.PI * 2; // Kopf-Mitte
    const aF2 = ((i + 0.8) / zaehne) * Math.PI * 2; // Kopf-Ende
    const a1 = ((i + 1) / zaehne) * Math.PI * 2; // Fuß-Ende
    teile.push(
      `${i === 0 ? "M" : "L"} ${p(fuss, a0)}` +
        ` Q ${p(kopf, aF1 - 0.02)} ${p(kopf, aF1)}` +
        ` Q ${p(kopf + 1.2, aK)} ${p(kopf, aF2)}` +
        ` Q ${p(kopf, aF2 + 0.02)} ${p(fuss, a1)}`,
    );
  }
  return `${teile.join(" ")} Z`;
}

/** Ein einzelnes, poliertes Zahnrad (SVG-<g>, ohne eigene Rotation — die
 *  setzt der Aufrufer). Kranz mit Tiefe (Doppelring), verjüngte Speichen,
 *  Naben-Juwel mit Glow. */
function Zahnrad({
  r,
  zaehne,
  hex,
  steht,
  speichen = 5,
}: {
  r: number;
  zaehne: number;
  hex: string;
  steht: boolean;
  speichen?: number;
}) {
  const o = steht ? 0.35 : 0.92;
  return (
    <g>
      {/* Zahnkranz: dünner Schatten-Ring außen (Tiefe) + Voll-Kranz. */}
      <path d={zahnradPfad(r + 0.6, zaehne)} fill="none" stroke="rgba(11,11,12,0.6)" strokeWidth={2.4} opacity={0.5} />
      <path d={zahnradPfad(r, zaehne)} fill="none" stroke={hex} strokeWidth={1.5} opacity={o} strokeLinejoin="round" />
      {/* Innerer Laufring (Radkörper) — gibt dem Rad Fläche/Tiefe. */}
      <circle r={r - 4} fill="none" stroke={hex} strokeWidth={0.6} opacity={o * 0.45} />
      {/* Verjüngte Speichen (breit an der Nabe, schmal am Kranz) + Bohrung. */}
      {Array.from({ length: speichen }).map((_, s) => {
        const a = (s / speichen) * Math.PI * 2;
        const cos = Math.cos(a);
        const sin = Math.sin(a);
        const nx = cos * 3.4;
        const ny = sin * 3.4;
        const ex = cos * (r - 4);
        const ey = sin * (r - 4);
        // Keil-Speiche als schmales Dreieck.
        const px = -sin * 1.4;
        const py = cos * 1.4;
        return (
          <g key={s}>
            <path
              d={`M ${(nx + px).toFixed(1)} ${(ny + py).toFixed(1)} L ${ex.toFixed(1)} ${ey.toFixed(1)} L ${(nx - px).toFixed(1)} ${(ny - py).toFixed(1)} Z`}
              fill={hex}
              opacity={o * 0.55}
            />
            <circle cx={cos * (r * 0.6)} cy={sin * (r * 0.6)} r={1.5} fill="none" stroke={hex} strokeWidth={0.5} opacity={o * 0.5} />
          </g>
        );
      })}
      {/* Naben-Juwel mit Glow: Ring + heller Kern. */}
      <circle r={4.2} fill="rgba(11,11,12,0.85)" stroke={hex} strokeWidth={1} opacity={o} />
      <circle r={2} fill={hex} opacity={steht ? 0.4 : 0.85} />
      <circle r={0.9} fill="#fffaf0" opacity={steht ? 0.3 : 0.8} />
    </g>
  );
}

// K7/Uhrwerk-Klarheit: verständliches Label statt abstraktem "U/MIN".
// pace = Umsatz-YTD / (VJ-Gesamt × Tag-im-Jahr); 1.0 = exakt auf VJ-Kurs.
function paceLabel(pace: number): string {
  const pct = Math.round(pace * 100);
  const pfeil = pace >= 1.0 ? "▲" : pace < 0.9 ? "▼" : "→";
  return `${pct} % VJ ${pfeil}`;
}

function paceFarbe(pace: number | null): string {
  if (pace == null) return "text-leitstand-dim";
  if (pace >= 1.0) return "text-signal-green";
  if (pace >= 0.9) return "text-signal-amber";
  return "text-signal-clay";
}

// Echter Planetensatz (Kevin 06.07.: "muss realistisch"): das Holding-Rad ist
// das Sonnenrad, die drei Firmen-Räder sind Planeten. Physikalisch korrekt:
//  · GLEICHES Modul (Zahngröße) bei allen Rädern → r = z · MODUL/2. Nur so
//    greifen die Zähne (gleich große Zähne).
//  · Achsabstand = Summe der Teilkreisradien (Zähne genau im Eingriff).
//  · Drehzahl-Verhältnis ω_planet/ω_sonne = z_sonne/z_planet, GEGENLÄUFIG
//    (außenverzahnt). Kleines Rad dreht schneller — wie im echten Getriebe.
//  · Phasenlage so gesetzt, dass am Eingriffspunkt Zahn auf Lücke trifft.
// Der Satz läuft im Takt der GRUPPE (eine gekoppelte Mechanik kann keine
// Einzel-Drehzahlen haben); ein Rad hinter Plan SCHLUPFT (stockt) — die
// einzige ehrliche Art, Minderleistung in einem starren Getriebe zu zeigen.
const MODUL = 3.75;
const rFor = (z: number): number => (z * MODUL) / 2;
const SUN_Z = 16;
const SUN = { x: 120, y: 84, r: rFor(SUN_Z), z: SUN_Z };

type PlanetFirma = Extract<FirmaSlug, "drainx" | "gundk" | "infrax">;
interface Planet {
  z: number;
  r: number;
  x: number;
  y: number;
  phaseDeg: number;
  periodFactor: number; // T_planet = T_sonne · z_planet/z_sonne
}
const PLANET_DEF: { firma: PlanetFirma; z: number; alphaDeg: number }[] = [
  { firma: "drainx", z: 10, alphaDeg: 152 },
  { firma: "gundk", z: 12, alphaDeg: 28 },
  { firma: "infrax", z: 8, alphaDeg: 268 },
];
const PLANETEN: Record<PlanetFirma, Planet> = Object.fromEntries(
  PLANET_DEF.map((d) => {
    const r = rFor(d.z);
    const dist = SUN.r + r;
    const a = (d.alphaDeg * Math.PI) / 180;
    return [
      d.firma,
      {
        z: d.z,
        r,
        x: SUN.x + dist * Math.cos(a),
        y: SUN.y + dist * Math.sin(a),
        // Eingriffs-Phase: Zahn (Sonne) trifft Lücke (Planet) am Achsstrahl.
        phaseDeg: d.alphaDeg * (1 + SUN_Z / d.z) + 180 / d.z,
        periodFactor: d.z / SUN_Z,
      },
    ];
  }),
) as Record<PlanetFirma, Planet>;

export default function Uhrwerk({ raeder, stale, onFirma }: Props) {
  // Anlaufen mit Trägheit: Räder starten pausiert, laufen gestaffelt an.
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => {
    const t = window.setTimeout(() => setLaeuft(true), 400);
    return () => window.clearTimeout(t);
  }, []);

  // Stocken: unter Plan → ~1s Pause alle ~5s (Ruckler statt harter Stand).
  const [gestockt, setGestockt] = useState<Record<string, boolean>>({});
  const stockend = useRef(raeder.filter((r) => r.paceRatio != null && r.paceRatio < 0.9));
  stockend.current = raeder.filter((r) => r.paceRatio != null && r.paceRatio < 0.9);
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const iv = setInterval(() => {
      const betroffen = stockend.current;
      if (betroffen.length === 0) return;
      const ziel = betroffen[Math.floor(Math.random() * betroffen.length)].firma;
      setGestockt((g) => ({ ...g, [ziel]: true }));
      window.setTimeout(() => setGestockt((g) => ({ ...g, [ziel]: false })), 1000);
    }, 5000);
    return () => clearInterval(iv);
  }, []);

  const stockerZeile = raeder.find((r) => r.paceRatio != null && r.paceRatio < 0.9);

  // K7/Uhrwerk-Klarheit (Kevin 06.07.): Gruppen-Pace = Ø der Whitelist-Firmen
  // (drainx/gundk; R1: InfraX bleibt aus dem Konzern-Aggregat, dreht aber als
  // eigenes Rad mit). Zeigt in EINER Zahl, wie rund die Gruppe läuft.
  const konzernPaces = raeder
    .filter((r) => r.firma !== "infrax" && r.paceRatio != null)
    .map((r) => r.paceRatio as number);
  const gruppenPace =
    konzernPaces.length > 0
      ? konzernPaces.reduce((a, b) => a + b, 0) / konzernPaces.length
      : null;

  // Takt des Getriebes = Gruppen-Pace: schnellere Gruppe → kürzere Umlaufzeit
  // des Sonnenrads. Die Planeten folgen im Zähne-Verhältnis (starr gekoppelt).
  const sonnePeriodeS =
    gruppenPace != null && gruppenPace > 0
      ? Math.min(44, Math.max(10, 24 / gruppenPace))
      : 24;

  return (
    <div aria-label="Uhrwerk — Umsatz-Tempo der Gruppe gegenüber Vorjahr">
      <header className="flex items-baseline justify-between gap-2">
        <div className="label-tracked">Uhrwerk · Umsatz-Tempo</div>
        <div className={`num-mono text-[10px] ${paceFarbe(gruppenPace)}`}>
          {stale
            ? "STAND GESTERN"
            : gruppenPace != null
              ? `GRUPPE ${paceLabel(gruppenPace)}`
              : "—"}
        </div>
      </header>
      {/* Ein-Satz-Legende: macht das Instrument in 5 Sekunden lesbar. */}
      <p className="text-[10px] text-leitstand-dim mb-1">
        Das Getriebe läuft im Takt der Gruppe (ggü. Vorjahr) · ein Rad hinter
        Plan schlupft. Zahlen je Firma unten.
      </p>

      <svg viewBox="0 0 240 150" width="100%" aria-hidden>
        {/* Sonnenrad (Holding) in der Mitte — treibt den Satz im Gruppen-Takt. */}
        <g transform={`translate(${SUN.x} ${SUN.y})`}>
          <g
            className="uhrwerk-rad"
            style={{
              animationDuration: `${sonnePeriodeS}s`,
              animationPlayState: laeuft ? "running" : "paused",
            }}
          >
            <Zahnrad r={SUN.r} zaehne={SUN.z} hex="#6366f1" steht={false} speichen={6} />
          </g>
          {/* Rubinlager: rote Kern-Bohrung über der Indigo-Nabe. */}
          <circle r={2.6} fill="#e11d48" className="uhrwerk-rubin" />
          <circle r={1} fill="#fff1f2" opacity={0.85} />
          <circle
            r={SUN.r + 4}
            fill="transparent"
            className="cursor-pointer"
            onClick={() => onFirma("xholding")}
            role="button"
            aria-label="X-Holding öffnen"
          />
        </g>

        {/* Planeten-Räder (Firmen) im Zahneingriff: gegenläufig, Drehzahl im
            Zähne-Verhältnis, Phase auf Zahn-in-Lücke gesetzt. Schlupft (stockt)
            wenn die Firma hinter Plan liegt. */}
        {raeder.map((rad) => {
          const p = PLANETEN[rad.firma as PlanetFirma] ?? PLANETEN.drainx;
          const palette = FIRMA_COLORS[rad.firma];
          const steht = rad.paceRatio == null;
          // Umlaufzeit im starren Verhältnis zum Sonnenrad (nicht Einzel-Pace).
          const periode = sonnePeriodeS * p.periodFactor;
          return (
            <g key={rad.firma} transform={`translate(${p.x} ${p.y})`}>
              <g
                className="uhrwerk-rad"
                style={{
                  animationDuration: `${periode}s`,
                  animationPlayState:
                    !laeuft || steht || gestockt[rad.firma] ? "paused" : "running",
                  animationDirection: "reverse",
                }}
              >
                {/* Statische Eingriffs-Phase (die Animation dreht darüber). */}
                <g transform={`rotate(${p.phaseDeg.toFixed(1)})`}>
                  <Zahnrad r={p.r} zaehne={p.z} hex={palette.hex} steht={steht} />
                </g>
              </g>
              <circle
                r={p.r + 4}
                fill="transparent"
                className="cursor-pointer"
                onClick={() => onFirma(rad.firma)}
                role="button"
                aria-label={`${FIRMA_LABEL[rad.firma]} öffnen`}
              />
            </g>
          );
        })}
      </svg>

      {/* Legende (HTML → überlappt nie): Farbpunkt + Firma + Pace, klickbar. */}
      <ul className="mt-1.5 grid grid-cols-2 gap-x-4 gap-y-1">
        {[
          ...raeder.map((rad) => ({
            key: rad.firma,
            hex: FIRMA_COLORS[rad.firma].hex,
            label: FIRMA_LABEL[rad.firma],
            pace: rad.paceRatio,
            onClick: () => onFirma(rad.firma),
          })),
          {
            key: "xholding",
            hex: "#6366f1",
            label: "X-Holding",
            pace: gruppenPace,
            gruppe: true,
            onClick: () => onFirma("xholding"),
          },
        ].map((c) => (
          <li key={c.key}>
            <button
              type="button"
              onClick={c.onClick}
              className="flex w-full items-center gap-1.5 text-left hover:opacity-80"
            >
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: c.hex }} aria-hidden />
              <span className="truncate text-[10px] text-leitstand-dim">{c.label}</span>
              <span className={`num-mono ml-auto text-[10px] ${paceFarbe(c.pace ?? null)}`}>
                {c.pace == null
                  ? "—"
                  : `${"gruppe" in c ? "Ø " : ""}${paceLabel(c.pace)}`}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {stockerZeile && (
        <p className="num-mono text-[10px] text-signal-clay">
          {FIRMA_LABEL[stockerZeile.firma].toUpperCase()}-RAD STOCKT —{" "}
          {(stockerZeile.paceRatio! * 100).toFixed(0)} % vom Vorjahres-Kurs,
          liegt hinter Plan.
        </p>
      )}
    </div>
  );
}
